import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Page, useToast } from "@theg1group/ui";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api.ts";
import { API_BASE } from "../config.ts";

interface Detail {
	message: Record<string, unknown>;
	received: Array<Record<string, unknown>>;
	dkim: Array<Record<string, unknown>>;
	auth: Array<Record<string, unknown>>;
	links: Array<Record<string, unknown> & { id: string; hops: Array<Record<string, unknown>> }>;
	roles: Array<{ role: string; source: string; fqdn: string }>;
	findings: Array<Record<string, unknown>>;
	observations?: Observation[];
}

interface Observation {
	id: string;
	provider: string;
	address: string;
	folder: string | null;
	placement_source: string | null;
	banners: Array<{ type?: string; text?: string; scope?: string }> | null;
	images_blocked: boolean | null;
	links_rewritten: Record<string, unknown> | null;
	clipped: boolean | null;
	list_screenshot_key: string | null;
	open_screenshot_key: string | null;
	collected_at: string;
}

const FOLDER_VARIANT: Record<string, "success" | "error" | "warning" | "info" | "default"> = {
	inbox: "success",
	spam: "error",
	junk: "error",
	promotions: "warning",
	updates: "info",
	social: "info",
};

const LAYERS: Record<string, string[]> = {
	"Visible in email": ["sender_from", "envelope_return", "message_id", "unsubscribe", "content_asset"],
	Infrastructure: ["auth_signing", "esp_infra", "tracking_pixel", "redirector"],
	Landing: ["landing"],
};

export function MessageDetail() {
	const { id } = useParams();
	const qc = useQueryClient();
	const toast = useToast();
	const { data, isLoading } = useQuery({
		queryKey: ["message", id],
		queryFn: () => api.get<Detail>(`/api/messages/${id}`),
		enabled: !!id,
	});

	const detonate = useMutation({
		mutationFn: (linkId: string) => api.post<{ landing: string | null }>(`/api/links/${linkId}/unroll`),
		onSuccess: (r) => {
			toast.addToast("success", r.landing ? `Landing: ${r.landing}` : "Unrolled");
			qc.invalidateQueries({ queryKey: ["message", id] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const detonateAll = useMutation({
		mutationFn: () => api.post<{ detonated: number; total: number; landings: string[] }>(`/api/messages/${id}/detonate`),
		onSuccess: (r) => {
			toast.addToast(
				"success",
				`Detonated ${r.detonated}/${r.total}${r.landings.length ? ` · landings: ${r.landings.join(", ")}` : ""}`,
			);
			qc.invalidateQueries({ queryKey: ["message", id] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	if (isLoading) return <Page title="Message">Loading…</Page>;
	if (!data) return <Page title="Message">Not found.</Page>;
	const m = data.message;

	return (
		<Page
			title={(m.subject as string) ?? "(no subject)"}
			description={`case / anchor: ${String(m.id)}`}
			actions={
				<div className="flex items-center gap-3">
					<Button
						size="sm"
						variant="outline"
						loading={detonateAll.isPending}
						onClick={() => detonateAll.mutate()}
					>
						Detonate all CTAs
					</Button>
					<Link to="/files" className="text-primary hover:underline text-sm">
						← Files
					</Link>
				</div>
			}
		>
			<Card className="mb-3.5">
				<CardBody>
				<div className="grid gap-2.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))" }}>
					<Field label="From" value={`${(m.from_display as string) ?? ""} <${(m.from_addr as string) ?? ""}>`} />
					<Field label="From domain" value={m.from_domain as string} link={`/domains/${m.from_domain}`} />
					<Field label="Return-Path" value={m.return_path as string} />
					<Field label="ESP" value={(m.esp_name as string) ?? (m.esp_key as string)} />
					<Field label="Date" value={m.date_sent as string} />
					<Field label="SPF" value={m.spf_result as string} />
					<Field label="DKIM" value={m.dkim_result as string} />
					<Field label="DMARC" value={m.dmarc_result as string} />
					<Field label="Campaign" value={m.campaign_id as string} />
					<Field label="Template hash" value={(m.template_hash as string)?.slice(0, 16)} />
				</div>
				</CardBody>
			</Card>

			{data.findings.length > 0 && (
				<Section title="Findings">
					{data.findings.map((f) => (
						<div key={String(f.id)} className="text-sm mb-1">
							<Badge variant="warning">{String(f.kind)}</Badge> <span className="text-content">{String(f.summary)}</span>
						</div>
					))}
				</Section>
			)}

			{data.observations && data.observations.length > 0 && (
				<Section title={`Provider rendering (${data.observations.length})`}>
					<div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
						{data.observations.map((o) => (
							<div key={o.id} className="rounded-md border border-line p-2.5">
								<div className="flex items-center justify-between gap-2 mb-1.5">
									<span className="text-sm font-semibold text-content">{o.provider}</span>
									{o.folder && <Badge variant={FOLDER_VARIANT[o.folder] ?? "default"}>{o.folder}</Badge>}
								</div>
								<div className="font-mono text-[11px] text-muted break-all mb-1.5">{o.address}</div>
								<div className="flex flex-wrap gap-1 mb-1.5">
									{o.images_blocked && <Badge variant="warning">images blocked</Badge>}
									{o.clipped && <Badge variant="warning">clipped</Badge>}
									{o.links_rewritten && Object.keys(o.links_rewritten).length > 0 && (
										<Badge variant="info">links rewritten</Badge>
									)}
									{o.placement_source && <Badge>{o.placement_source}</Badge>}
								</div>
								{o.banners?.map((b, i) => (
									<div key={`${o.id}-b${i}`} className="text-xs text-content mb-0.5">
										<Badge variant="warning">{b.scope ?? "open"}</Badge> {b.text ?? b.type}
									</div>
								))}
								<div className="flex gap-2 mt-2">
									{o.list_screenshot_key && (
										<Shot label="list" k={o.list_screenshot_key} />
									)}
									{o.open_screenshot_key && (
										<Shot label="open" k={o.open_screenshot_key} />
									)}
								</div>
							</div>
						))}
					</div>
				</Section>
			)}

			<Section title={`Relay chain (${data.received.length} hops)`}>
				{data.received.map((h) => (
					<div key={String(h.id)} className="font-mono text-xs text-content mb-0.5 break-all">
						<b>#{String(h.hop_index)}</b> {(h.from_host as string) ?? "?"}{" "}
						{h.from_ip ? `[${String(h.from_ip)}]` : ""} → by {(h.by_host as string) ?? "?"}{" "}
						{h.tls ? `· ${String(h.tls)}` : ""} {h.ts ? `· ${String(h.ts)}` : ""}
					</div>
				))}
			</Section>

			<Section title="DKIM signatures">
				{data.dkim.map((d) => (
					<div key={String(d.id)} className="font-mono text-xs text-content mb-0.5">
						d={String(d.domain)} · s={String(d.selector ?? "—")} · a={String(d.algo ?? "—")} ·{" "}
						{String(d.result ?? "—")}
					</div>
				))}
			</Section>

			<Section title="Authentication results">
				{data.auth.map((a) => (
					<div key={String(a.id)} className="font-mono text-xs text-content mb-0.5">
						{String(a.method)}={String(a.result ?? "—")}{" "}
						{a.header_i ? `· i=${String(a.header_i)}` : ""} {a.header_s ? `· s=${String(a.header_s)}` : ""}
					</div>
				))}
			</Section>

			<Section title="Host roles">
				{Object.entries(LAYERS).map(([layer, roles]) => {
					const hosts = data.roles.filter((r) => roles.includes(r.role));
					if (!hosts.length) return null;
					return (
						<div key={layer} className="mb-2">
							<div className="text-[11px] uppercase text-muted mb-1">{layer}</div>
							{hosts.map((r) => (
								<div key={`${r.role}:${r.fqdn}`} className="text-sm mb-0.5">
									<Badge>{r.role}</Badge>{" "}
									<Link to={`/domains/${encodeURIComponent(r.fqdn)}`} className="text-primary hover:underline">
										{r.fqdn}
									</Link>
								</div>
							))}
						</div>
					);
				})}
			</Section>

			<Section title={`Links (${data.links.length})`}>
				{data.links.map((l) => (
					<div key={l.id} className="mb-2.5 flex items-start justify-between gap-3">
						<div>
							<div className="text-sm">
								<Badge>{String(l.kind)}</Badge> <span className="text-content">{(l.anchor_text as string) ?? ""}</span>
							</div>
							<div className="font-mono text-xs text-muted break-all">
								{l.hops.map((h, i) => (
									<span key={String(h.id)}>
										{i > 0 ? " → " : ""}
										{(h.fqdn as string) ?? hostname(String(h.url))}
										{h.is_landing ? " 🏁" : ""}
									</span>
								))}
							</div>
						</div>
						{l.kind === "cta" && (
							<Button
								size="sm"
								variant="outline"
								loading={detonate.isPending && detonate.variables === l.id}
								onClick={() => detonate.mutate(l.id)}
							>
								Detonate
							</Button>
						)}
					</div>
				))}
			</Section>
		</Page>
	);
}

function Shot({ label, k }: { label: string; k: string }) {
	const src = `${API_BASE}/api/observations/screenshot?key=${encodeURIComponent(k)}`;
	return (
		<a href={src} target="_blank" rel="noreferrer" className="block">
			<div className="text-[11px] text-muted mb-0.5">{label}</div>
			<img src={src} alt={`${label} screenshot`} className="h-24 w-auto rounded border border-line object-cover object-top" />
		</a>
	);
}

function hostname(url: string): string {
	try {
		return new URL(url).hostname;
	} catch {
		return url.slice(0, 40);
	}
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<Card className="mb-3.5">
			<CardBody>
				<h2 className="text-sm font-semibold text-content mb-2.5">{title}</h2>
				{children}
			</CardBody>
		</Card>
	);
}

function Field({ label, value, link }: { label: string; value?: string | null; link?: string }) {
	if (!value || value === " <>") return null;
	return (
		<div>
			<div className="text-[11px] text-muted">{label}</div>
			<div className="text-sm text-content break-words">
				{link ? (
					<Link to={link} className="text-primary hover:underline">
						{value}
					</Link>
				) : (
					value
				)}
			</div>
		</div>
	);
}
